import {
    Box,
    FormControl,
    FormHelperText,
    OutlinedInput,
    Typography,
} from '@mui/material'
import TruncatedText from './TruncatedText'
import { AttributeType } from '../../constants/constants'
import { useTranslation } from 'react-i18next'
import { forwardRef } from 'react'

interface TextInputProps {
    id: string
    label: string
    value: string
    onChange: any
    type: string
    required: boolean
    placeholder?: string
    disabled?: boolean
    errorMessage?: string
    endAdornment?: any
    range?: string
    units?: string
}

const NumberInput = forwardRef<HTMLInputElement, TextInputProps>(
    (props, ref) => {
        const {
            id,
            label,
            value,
            onChange,
            type,
            required,
            placeholder,
            disabled,
            errorMessage = '',
            endAdornment,
            range = '',
        } = props
        const { t } = useTranslation()

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            // Allow only positive integers
            onChange(id, newValue)
        }
        const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const { key, currentTarget } = e

            // Allow control keys like Backspace, Tab, and Arrow keys
            if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(key)) {
                return
            }

            const inputValue = currentTarget.value

            // Allow digits and a single dot for decimals
            if (key === '.' && type.toLowerCase() === AttributeType.NUMBER) {
                e.preventDefault()
            } else if (/[\d.]/.test(key)) {
                // Prevent multiple dots
                if (key === '.' && inputValue.includes('.')) {
                    e.preventDefault()
                }
            }
            // Allow a single '-' at the start for negative numbers
            else if (key === '-' && inputValue === '') {
                return
            }
            // Block all other inputs
            else {
                e.preventDefault()
            }
        }

        return (
            <Box sx={{ width: '100%' }}>
                <FormControl fullWidth>
                    <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                        {label}
                        {required ? '*' : ''}
                    </Typography>
                    <OutlinedInput
                        size="small"
                        id={id}
                        label=""
                        type="text"
                        value={value}
                        inputRef={ref}
                        onKeyDown={handleKeyPress}
                        onChange={handleChange}
                        sx={{
                            width: '100%',
                            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                {
                                    display: 'none',
                                },
                            '& input[type=number]': {
                                MozAppearance: 'textfield',
                            },
                            '& ::placeholder': {
                                // opacity: 0.14,
                            },
                        }}
                        placeholder={
                            placeholder
                                ? placeholder
                                : t('enterLabel', { label })
                        }
                        inputProps={{
                            style: {
                                fontSize: 14,
                                opacity:
                                    value !== '' && value !== null
                                        ? '1'
                                        : '0.4',
                            },
                            type: `${type}`,
                            onKeyPress: (e) => {
                                if (e.key === 'Enter') {
                                    // onEnter(
                                    //     id,
                                    //     (e.target as HTMLInputElement).value,
                                    // )

                                    // write your functionality here
                                    e.preventDefault()
                                }
                            },
                        }}
                        disabled={!!disabled}
                        error={errorMessage !== ''}
                        endAdornment={
                            endAdornment ? (
                                <Typography
                                    sx={{
                                        opacity: disabled ? '0.5' : '0.7',
                                        fontSize: '14px',
                                    }}
                                >
                                    {endAdornment}
                                </Typography>
                            ) : (
                                ''
                            )
                        }
                    />
                    {range?.trim() && (
                        <FormHelperText>
                            <TruncatedText
                                sx={{ fontSize: '12px' }}
                                text={`Accepted values (${range})`}
                            />
                        </FormHelperText>
                    )}
                    {errorMessage !== '' && (
                        <FormHelperText sx={{ color: 'red' }}>
                            {errorMessage}
                        </FormHelperText>
                    )}
                </FormControl>
            </Box>
        )
    },
)
NumberInput.displayName = 'NumberInput'

export default NumberInput
